import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Footprints, Loader2, MessageCircle, Send } from "lucide-react";
import { chat } from "../lib/api";

export const PathSteps = ({ journeyId, latest, fs }) => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);

  const send = async () => {
    if (!input.trim() || loading) return;
    const next = [...messages, { role: "user", content: input.trim() }];
    setMessages(next);
    setInput("");
    setLoading(true);
    fs.ensureAged();
    try {
      const { reply } = await chat(journeyId, next[next.length - 1].content, next.slice(-6), fs.years);
      setMessages([...next, { role: "assistant", content: reply }]);
    } catch {
      setMessages([...next, { role: "assistant", content: "The echo stuttered. Try again." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="glass rounded-3xl p-6 mt-5" data-testid="path-steps">
      <div className="flex items-center justify-between mb-4">
        <div className="font-mono-label text-white/50 flex items-center gap-2">
          <Footprints size={14} /> Your step-by-step path
        </div>
        <button onClick={() => navigate(`/chat/${journeyId}`)} className="btn-pill text-xs !py-1.5 !px-3" data-testid="chat-btn">
          <MessageCircle size={12} /> Future You
        </button>
      </div>

      {!latest ? (
        <p className="text-sm text-white/40">Pick a pathway and your steps to reach it will appear here.</p>
      ) : (
        <>
          <div className="text-lg font-display mb-3">{latest.chosen_title}</div>
          <ol className="space-y-3" data-testid="path-steps-list">
            {(latest.steps || []).map((s, k) => (
              <li key={k} className="flex gap-3 items-start">
                <span className="w-6 h-6 rounded-full border border-cyan-300/60 text-cyan-200 text-xs flex items-center justify-center shrink-0 shadow-[0_0_10px_rgba(0,240,255,0.3)]">
                  {k + 1}
                </span>
                <span className="text-sm text-white/85 leading-relaxed">{s}</span>
              </li>
            ))}
          </ol>
        </>
      )}

      <div className="mt-6 pt-5 border-t border-white/10">
        <p className="text-xs text-amber-200/80 mb-3">Have more questions? Ask your future self at {fs.futureAgeLabel().toLowerCase()}.</p>
        <div className="space-y-2 max-h-56 overflow-y-auto mb-3" data-testid="path-chat-messages">
          {messages.map((m, i) => (
            <div
              key={i}
              className={`text-sm rounded-xl px-3 py-2 leading-relaxed ${m.role === "user" ? "bg-cyan-400/10 border border-cyan-400/25 ml-6" : "border border-amber-300/25 bg-amber-300/[0.04] text-amber-50/90 mr-6"}`}
            >
              {m.content}
            </div>
          ))}
          {loading && <div className="text-xs text-white/50 flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Future you is thinking…</div>}
        </div>
        <div className="flex gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Ask your future self…"
            data-testid="path-chat-input"
            className="flex-1 bg-white/[0.03] border border-white/10 rounded-full px-4 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-amber-300/60"
          />
          <button onClick={send} disabled={loading} className="btn-pill btn-pill-gold !py-2 !px-3" data-testid="path-chat-send">
            <Send size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};
